import { format } from "date-fns";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { CellState, shifts, shiftHours } from "./schedule-calendar-utils";

export interface DayScheduleDto {
  date: string;
  isHoliday?: boolean;
  holidayName?: string | null;
}

interface ScheduleShiftGridProps {
  days: Date[];
  grid: Record<string, CellState>;
  editMode: boolean;
  scheduleData: DayScheduleDto[];
  toggleCell: (dateStr: string, shift: string) => void;
  setGrid: React.Dispatch<React.SetStateAction<Record<string, CellState>>>;
}

/** Weekly AM/PM/OT availability grid shared between technician and machine schedule pages. */
export function ScheduleShiftGrid({
  days,
  grid,
  editMode,
  scheduleData,
  toggleCell,
  setGrid
}: ScheduleShiftGridProps) {
  return (
    <div className="aero:space-y-4">
      <table className="aero:w-full aero:border-collapse aero:text-sm aero:table-fixed aero:rounded-lg aero:overflow-hidden">
        <colgroup>
          <col className="aero:w-[12.5%]" />
          {days.map((_, idx) => (
            <col key={idx} className="aero:w-[12.5%]" />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="aero:border aero:border-neutral-200 aero:p-3 aero:bg-neutral-50 aero:text-center aero:font-medium aero:text-neutral-700">
              Shift
            </th>
            {days.map((d) => {
              const dateStr = format(d, "yyyy-MM-dd");
              const dayDto = scheduleData.find((x) => x.date === dateStr);
              const isWeekend = [0, 6].includes(d.getDay());
              return (
                <th
                  key={d.toISOString()}
                  className={cn(
                    "aero:border aero:border-neutral-200 aero:p-3 aero:text-center",
                    dayDto?.isHoliday
                      ? "aero:bg-yellow-200"
                      : "aero:bg-neutral-50"
                  )}
                >
                  <div className="aero:flex aero:flex-col aero:items-center aero:gap-0.5">
                    <span className="aero:font-medium aero:text-neutral-700">
                      {format(d, "EEE")}
                    </span>
                    <span className="aero:text-neutral-600">
                      {format(d, "MMM d")}
                    </span>
                    {dayDto?.isHoliday ? (
                      <span className="aero:text-[10px] aero:text-amber-700 aero:font-medium">
                        {dayDto.holidayName || "Holiday"}
                      </span>
                    ) : isWeekend ? (
                      <span className="aero:text-[10px] aero:text-neutral-400">
                        Weekend
                      </span>
                    ) : null}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {shifts.map((shift) => (
            <tr key={shift}>
              <td className="aero:border aero:border-neutral-200 aero:p-3 aero:text-center aero:bg-neutral-50">
                <div className="aero:flex aero:flex-col aero:items-center aero:justify-center">
                  <span className="aero:font-medium aero:text-neutral-700">
                    {shift}
                  </span>
                  <span className="aero:text-[11px] aero:text-neutral-500">
                    {shiftHours[shift]}
                  </span>
                </div>
              </td>
              {days.map((d) => {
                const dateStr = format(d, "yyyy-MM-dd");
                const key = `${dateStr}_${shift}`;
                const state = grid[key];
                const dayDto = scheduleData.find((x) => x.date === dateStr);
                const isWeekend = [0, 6].includes(d.getDay());
                const isHoliday = !!dayDto?.isHoliday;
                const isWeekendOrHoliday = isWeekend || isHoliday;
                // OT (22:00–06:00) is unavailable by default on all days, like weekends.
                const defaultAvailable = !isWeekendOrHoliday && shift !== "OT";
                const isAvailable = state?.available ?? defaultAvailable;
                const isOverrideNow = state
                  ? state.available !== defaultAvailable
                  : false;

                let bgClass: string;
                if (isAvailable) {
                  bgClass = "aero:bg-green-50 aero:hover:bg-green-100";
                } else if (isOverrideNow) {
                  // Explicitly set unavailable by the admin
                  bgClass = "aero:bg-red-50 aero:hover:bg-red-100";
                } else {
                  // Default unavailable: weekend, holiday, or OT — matches the calendar
                  // weekend grey (WOR-2187).
                  bgClass = "aero:bg-gray-200 aero:hover:bg-gray-300";
                }

                return (
                  <td
                    key={key}
                    onClick={() => !editMode && toggleCell(dateStr, shift)}
                    className={cn(
                      "aero:border aero:border-neutral-200 aero:p-3 aero:cursor-pointer aero:select-none aero:transition-colors",
                      bgClass,
                      state?.dirty
                        ? "aero:ring-2 aero:ring-blue-500 aero:ring-inset"
                        : ""
                    )}
                  >
                    <div className="aero:flex aero:flex-col aero:items-center aero:justify-center aero:gap-1">
                      <span className="aero:text-xs aero:font-medium">
                        {isAvailable ? (
                          <span className="aero:text-green-700">
                            ✓ Available
                          </span>
                        ) : (
                          <span
                            className={cn(
                              !isOverrideNow
                                ? "aero:text-neutral-600"
                                : "aero:text-red-700"
                            )}
                          >
                            ✗ Unavailable
                          </span>
                        )}
                      </span>
                      {editMode && (
                        <Switch
                          checked={isAvailable}
                          onCheckedChange={(checked) => {
                            setGrid((g) => {
                              const current = g[key];
                              if (!current) return g;
                              return {
                                ...g,
                                [key]: {
                                  ...current,
                                  available: checked,
                                  dirty:
                                    current.originalAvailable !== checked ||
                                    current.dirty
                                }
                              };
                            });
                          }}
                          className="aero:scale-75"
                        />
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
